import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Stack,
  Button,
  Tooltip,
  Popover,
  MenuItem,
  ListItemText,
  Menu,
  ListItemIcon,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
} from "@mui/material";
import CEDatePicker from "components/CEDatePicker";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import useEntityUpdates from "hooks/useEntityUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";

import {
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive, {
  currencyQuickFilterFn,
} from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import Grid from "@mui/material/Grid2";
import Dialog from "components/Dialog";
import dayjs from "dayjs";
import { CONST } from "constants/CONST";
import { usePageTitle } from "hooks/usePageTitle";
import { numericComparator } from "helpers/ServiceHelpers";
import { NAVBAR_HEIGHT } from "constants/layout";

// Batch C wave 2 — Invoices columns whose desktop header uses
// ColumnDateFilter (hierarchical date filter) instead of ColumnFilter. Used to
// build the card-mode filter-sheet entries with identical semantics.
const CARD_MODE_DATE_FILTER_FIELDS = [
  "Date",
  "DueDate",
  "PaymentReceivedDate",
];

const Invoices = () => {
  usePageTitle("Invoices");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("INVOICES_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("INVOICES_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'
  const [overlayAnchorEl, setOverlayAnchorEl] = useState(null);

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("INVOICES_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "INVOICES_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const { setOverlayData, setOverlayName, showSnackbar, refreshPage } =
    useStateContext();
  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for invoices list to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to Invoice list so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "Invoice",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [minMaxDates, setMinMaxDates] = useState();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.INVOICES_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data
  const currentYear = dayjs().year();

  // Get saved year from localStorage or use current year as default
  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.INVOICES_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  // Initialize with current year, will be updated when minMaxDates is available
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfClientsOPIMinimums, setListOfClientsOPIMinimums] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  const [contextMenu, setContextMenu] = useState(null);
  const [listOfInvoices, setListOfInvoices] = useState(null);

  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  // Round-7 tap-bulletproofing (Amine, 4th on-device report): the long-press
  // ghost-click gate is a TIME WINDOW, not a boolean. A missed clear path on
  // a boolean swallowed the next tap forever; a timestamp expires
  // structurally shortly after the long-press that opened it.
  const suppressClickUntilRef = useRef(0);
  // Stamps the last touchend so the row handlers can tell the synthetic
  // mouse events that follow a touch (mousedown → mouseup → click) apart
  // from a genuine new press.
  const suppressTouchGraceRef = useRef(0);
  const LONG_PRESS_MS = 500;
  // Round-5 tap fix (Amine: "I tapped an item card, and it didn't open"):
  // a long-press suppress guard whose ghost click never arrived (Android
  // contextmenu, drag-off, pointercancel) must never swallow the NEXT tap.
  // A mousedown older than this since the last touchend is a real press.
  const GHOST_MOUSE_MS = 1000;
  // Round-7: window for the ghost click that follows a long-press — it
  // lands within a few frames of the release; 700ms covers slow devices
  // and can never block a later, genuine tap.
  const GHOST_CLICK_WINDOW_MS = 700;

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `INVOICES_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    const fields = [
      "Paid",
      "Number",
      "Client",
      "Date",
      "DueDate",
      "DueDate2",
      "PaymentReceivedDate",
      "Amount",
      "OPI",
      "Note",
    ];

    fields.forEach((field) => {
      let values = loadColumnFilter(field);

      // One-time migration: the Status filter used to offer "Overdue"
      // (>30 days late). The bucket is now "Past Due" (any days late) —
      // a saved "Overdue" value would match nothing and silently empty
      // the grid, so remap it on load.
      if (field === "Paid" && Array.isArray(values) && values.length > 0) {
        const migrated = values.map((value) =>
          value === "Overdue" ? "Past Due" : value,
        );
        if (migrated.some((value, index) => value !== values[index])) {
          saveColumnFilter(field, migrated);
        }
        values = migrated;
      }

      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter, saveColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "Paid",
      "Number",
      "Client",
      "Date",
      "DueDate",
      "DueDate2",
      "PaymentReceivedDate",
      "Amount",
      "OPI",
      "Note",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState(dayjs());
  const [markPaidTargets, setMarkPaidTargets] = useState([]);
  const [isMarkPaidSubmitting, setIsMarkPaidSubmitting] = useState(false);

  const [unmarkPaidDialogOpen, setUnmarkPaidDialogOpen] = useState(false);
  const [unmarkPaidTargets, setUnmarkPaidTargets] = useState([]);
  const [unmarkPaidReason, setUnmarkPaidReason] = useState("");
  const [isUnmarkPaidSubmitting, setIsUnmarkPaidSubmitting] = useState(false);

  const [updateDatesDialogOpen, setUpdateDatesDialogOpen] = useState(false);
  const [updateDatesDate, setUpdateDatesDate] = useState(dayjs());
  const [updateDatesTargets, setUpdateDatesTargets] = useState([]);
  const [isUpdateDatesSubmitting, setIsUpdateDatesSubmitting] = useState(false);

  const handleContextMenu = (event) => {
    event.preventDefault();

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    // New-press hygiene: a leftover long-press suppress guard (its ghost
    // click never came) must not swallow this press's click. The synthetic
    // mousedown that follows a touch keeps the guard — its click is still
    // on its way (see suppressTouchGraceRef).
    if (Date.now() - suppressTouchGraceRef.current > GHOST_MOUSE_MS) {
      suppressClickUntilRef.current = 0;
    }
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    // A new physical touch always starts clean: any earlier ghost click has
    // already fired (or the browser cancels it when a new touch arrives).
    // Without this, a stale guard swallows the very next card tap.
    suppressClickUntilRef.current = 0;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    suppressTouchGraceRef.current = Date.now();
  };

  // Auto-disarm (Amine 2026-10-05, round 6): when the ARMED selection model
  // empties through user toggling — the last selected card tapped OFF —
  // selection mode exits so the NEXT tap opens the card normally instead of
  // silently toggling again. The ref only lets the effect disarm after the
  // armed session has SEEN a non-empty model: the long-press arms
  // `selectionEnabled` and appends its id in the same callback, and the ref
  // guard keeps any transitional render (armed + empty model) from disarming
  // instantly.
  const armedSawSelectionRef = useRef(false);
  useEffect(() => {
    if (!selectionEnabled) {
      armedSawSelectionRef.current = false;
      return;
    }
    if (selectionModel.length > 0) {
      armedSawSelectionRef.current = true;
      return;
    }
    if (armedSawSelectionRef.current) {
      armedSawSelectionRef.current = false;
      setSelectionEnabled(false);
    }
  }, [selectionEnabled, selectionModel]);

  const openMarkPaidDialogForInvoices = (invoiceIds = []) => {
    if (!invoiceIds || invoiceIds.length === 0) return;
    setMarkPaidTargets(invoiceIds);
    setMarkPaidDate(dayjs());
    setMarkPaidDialogOpen(true);
  };

  const closeMarkPaidDialog = () => {
    if (isMarkPaidSubmitting) return;
    setMarkPaidDialogOpen(false);
    setMarkPaidTargets([]);
  };

  const openUnmarkPaidDialogForInvoices = (invoiceIds = []) => {
    if (!invoiceIds || invoiceIds.length === 0) return;
    setUnmarkPaidTargets(invoiceIds);
    setUnmarkPaidReason("");
    setUnmarkPaidDialogOpen(true);
  };

  const closeUnmarkPaidDialog = () => {
    if (isUnmarkPaidSubmitting) return;
    setUnmarkPaidDialogOpen(false);
    setUnmarkPaidTargets([]);
  };

  const openUpdateDatesDialogForInvoices = (invoiceIds = []) => {
    if (!invoiceIds || invoiceIds.length === 0) return;
    setUpdateDatesTargets(invoiceIds);
    setUpdateDatesDate(dayjs());
    setUpdateDatesDialogOpen(true);
  };

  const closeUpdateDatesDialog = () => {
    if (isUpdateDatesSubmitting) return;
    setUpdateDatesDialogOpen(false);
    setUpdateDatesTargets([]);
  };

  const handleConfirmUpdateDates = async () => {
    if (!updateDatesTargets.length) return;

    const newDate = (updateDatesDate || dayjs()).format("YYYY-MM-DD");
    const newDueDate = (updateDatesDate || dayjs())
      .add(30, "day")
      .format("YYYY-MM-DD");

    setIsUpdateDatesSubmitting(true);

    try {
      await api.put("/invoices/updateDates", {
        invoiceIds: updateDatesTargets,
        date: newDate,
        dueDate: newDueDate,
      });

      await fetchInvoicesData();
      setSelectionModel([]);
      setSelectionEnabled(false);
      closeUpdateDatesDialog();
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update invoice dates.";
      handleErrorDialog(message, theme);
    } finally {
      setIsUpdateDatesSubmitting(false);
    }
  };

  const markServicesPaidForInvoice = async (invoiceId) => {
    const invoiceResponse = await api.get(`/invoices/${invoiceId}`);
    const services = invoiceResponse?.data?.Services || [];

    if (!services.length) return;

    await Promise.all(
      services.map((service) =>
        api.post(`/services/markServicePaid`, {
          data: {
            id: service.id,
            Paid: true,
          },
        }),
      ),
    );
  };

  const handleConfirmMarkPaid = async () => {
    if (!markPaidTargets.length) return;

    const paymentDate = (markPaidDate || dayjs()).format("YYYY-MM-DD");

    setIsMarkPaidSubmitting(true);

    try {
      await api.put("/invoices/markPaid", {
        invoiceIds: markPaidTargets,
        paymentDate,
      });

      for (const invoiceId of markPaidTargets) {
        await markServicesPaidForInvoice(invoiceId);
      }

      await fetchInvoicesData();
      setSelectionModel([]);
      setSelectionEnabled(false);
      closeMarkPaidDialog();
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to mark invoices as paid.";
      handleErrorDialog(message, theme);
    } finally {
      setIsMarkPaidSubmitting(false);
    }
  };

  const handleConfirmUnmarkPaid = async () => {
    if (!unmarkPaidTargets.length) return;

    setIsUnmarkPaidSubmitting(true);

    try {
      // The server clears PaymentReceivedDate + PaymentMarkedPaidOn, resets
      // the linked Service.Paid flags, and writes an audit entry with the
      // optional reason. `confirm: true` is required or it returns 400.
      await api.put("/invoices/unmarkPaid", {
        invoiceIds: unmarkPaidTargets,
        confirm: true,
        reason: unmarkPaidReason.trim() || undefined,
      });

      await fetchInvoicesData();
      setSelectionModel([]);
      setSelectionEnabled(false);
      closeUnmarkPaidDialog();
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to mark invoices as unpaid.";
      handleErrorDialog(message, theme);
    } finally {
      setIsUnmarkPaidSubmitting(false);
    }
  };

  // Handle date selection changes (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Update state with all selected years
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years,
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.INVOICES_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.INVOICES_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchInvoicesData(updatedSelection.months, updatedSelection.years);
  };

  const fetchInvoicesData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);
      captureGridScroll();

      // Ensure we have valid parameters
      if (!months || months.length === 0) {
        months = [dayjs().format("MMM")];
        // Update both states to reflect the default value
        setSelectedMonths(months);
        setTempSelectedMonths(months);
      }

      if (!year || year.length === 0) {
        year = [String(currentYear)];
        // Update both states to reflect the default value
        setSelectedYear(year);
        setTempSelectedYear(year);
      }

      // Convert months and years to comma-separated strings if they're arrays
      const monthsParam = Array.isArray(months) ? months.join(",") : months;
      const yearsParam = Array.isArray(year) ? year.join(",") : year;

      api
        .get("/invoices", {
          params: {
            months: monthsParam,
            year: yearsParam,
          },
        })
        .then((response) => {
          if (response !== null) {
            //REVERSE LISTS
            const invoices = response.data;
            //invoices.reverse();
            setListOfInvoices(invoices);
            restoreGridScroll(invoices);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [
      selectedMonths,
      selectedYear,
      currentYear,
      handleErrorDialog,
      theme,
      captureGridScroll,
      restoreGridScroll,
    ],
  );

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
        api.get("/dashboard/serviceDateRange"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            clientsOPIMinimumsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            minMaxDatesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (clientsOPIMinimumsResponse) {
              setListOfClientsOPIMinimums(clientsOPIMinimumsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempLanguages = typesResponse.data.slice(0, 5);
              setListOfTypes(tempLanguages);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    // Initial data fetch
    fetchInvoicesData();

    // Get all various data lists
    getDataLists();
  }, [refreshPage, fetchInvoicesData, getDataLists]); // Don't include fetchInterpretationsData to prevent listener churn

  // Listener callback for invoices list refetch
  const handleInvoicesListRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      fetchInvoicesData(); // Refetch data
    },
    [fetchInvoicesData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = `Invoice updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEntityUpdates({
    entityType: "Invoice",
    entityId: "all",
    onUpdate: handleInvoicesListRefetch,
  });

  // Sync the temporary state with the actual state when the actual state changes from outside
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return (
      dayjs.tz(v1, "M/D/YYYY").valueOf() - dayjs.tz(v2, "M/D/YYYY").valueOf()
    );
  };

  // Compute the final total after any discount. Amount is stored as the
  // SUBTOTAL; the discount lives in the discount fields.
  const getInvoiceFinalTotal = useCallback((invoice) => {
    const subtotal = parseFloat(invoice?.Amount) || 0;
    let discount = 0;

    if (invoice?.DiscountType === "PERCENTAGE" && invoice?.DiscountPercentage) {
      discount = (subtotal * parseFloat(invoice.DiscountPercentage)) / 100;
    } else if (invoice?.DiscountAmount) {
      discount = Math.max(0, parseFloat(invoice.DiscountAmount));
    }

    return Math.max(0, subtotal - discount);
  }, []);

  const columns = [
    {
      field: "Paid",
      headerName: "Status",
      minWidth: 120,
      valueGetter: (_, params) => {
        // SAME rule as the totals strip (Paid | Unpaid | Past Due buckets):
        // Past Due = unpaid AND due date already passed — ANY days late, not
        // the old 30-day threshold. The column and the totals must never
        // disagree (a row showing "Not Paid" while the totals count it as
        // Past Due was the reported bug).
        if (params.PaymentReceivedDate) return "Paid";
        if (
          params.DueDate &&
          dayjs(params.DueDate).isBefore(dayjs(), "day")
        ) {
          return "Past Due";
        }
        return "Not Paid";
      },
      flex: 0.8,
      renderCell: (params) => {
        const status = params.value;
        const backgroundColor =
          status === "Paid"
            ? theme.palette.primary.main
            : status === "Past Due"
              ? theme.palette.error.main
              : theme.palette.warning.main;
        // "Not Paid" sits on the yellow badge in BOTH layouts — the text must
        // always be the dark warning shade. The old `&& !isMobile` guard
        // forced white text on mobile/card mode (unreadable yellow-on-white).
        const textColor =
          status === "Not Paid" ? theme.palette.warning[900] : "#fff";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                px: 1.5,
                py: 0.5,
                borderRadius: "12px",
                backgroundColor: backgroundColor,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: textColor,
                  fontWeight: "bold",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                {status}
              </Typography>
            </Box>
          </Box>
        );
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Number",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.Number;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client?.Account;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DueDate",
      headerName: "Due Date",
      valueGetter: (_, params) => {
        const data = params.DueDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DueDate2",
      headerName: "Late By",
      valueGetter: (_, params) => {
        // If already paid, not applicable
        if (params.PaymentReceivedDate) {
          return "N/A";
        }

        const dueDate = params.DueDate;
        if (!dueDate) return "N/A";

        const today = dayjs();
        const due = dayjs.tz(dueDate);
        const daysLate = today.diff(due, "day");

        if (daysLate <= 0) {
          return "N/A"; // Not late yet
        }

        return `${daysLate} day${daysLate === 1 ? "" : "s"}`;
      },
      sortComparator: numericComparator,
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentReceivedDate",
      headerName: "Payment Date",
      valueGetter: (_, params) => {
        const data = params.PaymentReceivedDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[7] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Amount",
      headerName: "Amount",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Amount;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const finalTotal = getInvoiceFinalTotal(params);
          value =
            finalTotal === 0
              ? "N/A"
              : `$${finalTotal.toLocaleString()}`;
        }

        return value;
      },
      // Quick filter parity (Amine 2026-10-01): "1500" — and "1,500" — match
      // the displayed "$1,500" on BOTH the desktop CEQuickFilter (the grid
      // consults this hook, verified in x-data-grid 7.29.12) and the mobile
      // card search (DataGridResponsive honors the same hook).
      getApplyQuickFilterFn: currencyQuickFilterFn,
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[8] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "OPI",
      headerName: "OPI",
      minWidth: 70,
      valueGetter: (_, params) => {
        return params.OPI ? "✓" : "";
      },
      flex: 0.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Note",
      headerName: "Note",
      valueGetter: (_, params) => {
        const data = params.Note;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 350,
      flex: 3.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[9] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.INVOICES_FILTER_KEY);
    if (!stored) return { items: [], quickFilterValues: [] };

    try {
      const parsed = JSON.parse(stored);
      // Column filtering on this screen runs through useColumnFilters and the
      // custom header filters. DataGrid `items` left in the persisted model
      // (from an older implementation / programmatic filter) have no visible
      // affordance here and silently hide rows — e.g. 342 invoices in the
      // totals but "1–10 of 10" in the grid. Keep only the quick filter,
      // which is visible in the toolbar and clearable through Clear Filters.
      return {
        items: [],
        quickFilterValues: Array.isArray(parsed?.quickFilterValues)
          ? parsed.quickFilterValues
          : [],
      };
    } catch (error) {
      return { items: [], quickFilterValues: [] };
    }
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.INVOICES_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Column definitions for filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "Paid",
        valueGetter: (_, row) => {
          // Same rule as the Status column and the totals strip: Past Due =
          // unpaid AND due date passed (any days), so the filter options and
          // the totals always describe the same buckets.
          if (row.PaymentReceivedDate) return "Paid";
          if (row.DueDate && dayjs(row.DueDate).isBefore(dayjs(), "day")) {
            return "Past Due";
          }
          return "Not Paid";
        },
      },
      {
        field: "OPI",
        valueGetter: (_, row) => {
          return row.OPI ? "✓" : "";
        },
      },
      { field: "Number", valueGetter: (_, row) => row.Number || "N/A" },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "DueDate",
        valueGetter: (_, row) =>
          row.DueDate ? dayjs.tz(row.DueDate).format("M/D/YYYY") : "N/A",
      },
      {
        field: "DueDate2",
        valueGetter: (_, row) => {
          // If already paid, not applicable
          if (row.PaymentReceivedDate) {
            return "N/A";
          }

          const dueDate = row.DueDate;
          if (!dueDate) return "N/A";

          const today = dayjs();
          const due = dayjs.tz(dueDate);
          const daysLate = today.diff(due, "day");

          if (daysLate <= 0) {
            return "N/A"; // Not late yet
          }

          return `${daysLate} day${daysLate === 1 ? "" : "s"}`;
        },
      },
      {
        field: "PaymentReceivedDate",
        valueGetter: (_, row) =>
          row.PaymentReceivedDate
            ? dayjs.tz(row.PaymentReceivedDate).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "Amount",
        valueGetter: (_, row) => {
          const data = row.Amount;
          if (data !== undefined && data !== null) {
            const finalTotal = getInvoiceFinalTotal(row);
            return finalTotal === 0
              ? "N/A"
              : `$${finalTotal.toLocaleString()}`;
          }
          return "N/A";
        },
      },
      { field: "Note", valueGetter: (_, row) => row.Note || "N/A" },
    ],
    [getInvoiceFinalTotal],
  );

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfInvoices || [], filterColumns);

  // "Mark As Unpaid" only makes sense for invoices that are actually paid.
  // Same truthiness rule as the grid's Paid valueGetter / totals strip, and
  // the ids come from the rows the grid is already rendering.
  const paidSelectedInvoiceIds = useMemo(() => {
    if (!selectionModel.length) return [];
    const selectedSet = new Set(selectionModel.map(String));
    return (columnFilteredData || [])
      .filter(
        (invoice) =>
          selectedSet.has(String(invoice.id)) &&
          Boolean(invoice.PaymentReceivedDate),
      )
      .map((invoice) => invoice.id);
  }, [selectionModel, columnFilteredData]);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfInvoices || listOfInvoices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfInvoices, loadAllColumnFilters]);

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter and localStorage
    const clearedModel = { items: [], quickFilterValues: [] };
    setFilterModel(clearedModel);
    localStorage.setItem(
      CONST.INVOICES_FILTER_KEY,
      JSON.stringify(clearedModel),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = listOfInvoices || [];
      } else {
        filteredData = (listOfInvoices || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [columnFilters, listOfInvoices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    const sanitizedModel = {
      items: [],
      quickFilterValues: Array.isArray(newFilterModel?.quickFilterValues)
        ? newFilterModel.quickFilterValues
        : [],
    };

    setFilterModel(sanitizedModel);
    localStorage.setItem(
      CONST.INVOICES_FILTER_KEY,
      JSON.stringify(sanitizedModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(CONST.INVOICES_SORT_KEY, JSON.stringify(newSortModel));
  };

  // Totals must describe the SAME rows the grid shows. Column filters are
  // applied by useColumnFilters (columnFilteredData); the quick filter is
  // reported by DataGrid's onStateChange (filterRowIds). Summing the raw
  // fetch instead made the strip contradict the grid: "Paid: 287" above
  // "1-10 of 10" whenever a saved column/quick filter was active.
  // Same convention as Interpretations and Expenses.
  // The quick filter's own values (not the id list) decide whether to
  // intersect: a zero-match quick filter yields an empty id list, which must
  // show zero totals, not fall back to the whole dataset.
  const quickFilterValues = Array.isArray(filterModel?.quickFilterValues)
    ? filterModel.quickFilterValues.filter(
        (value) => String(value ?? "").trim() !== "",
      )
    : [];
  const includeAllTotalsRows = quickFilterValues.length === 0;
  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));

  let totalPaid = 0;
  let countPaid = 0;

  let totalUnpaid = 0;
  let countUnpaid = 0;

  let totalBeyondDueDate = 0;
  let countBeyondDueDate = 0;

  const dataForTotals = columnFilteredData || [];
  const today = dayjs();

  dataForTotals.forEach((invoice) => {
    if (
      !includeAllTotalsRows &&
      !filteredRowIdsSet.has(String(invoice.id))
    ) {
      return;
    }

    const amount = getInvoiceFinalTotal(invoice);
    const dueDate = invoice.DueDate ? dayjs.tz(invoice.DueDate) : null;
    // Truthiness matches the grid's Paid valueGetter ("N/A" / "" /
    // undefined must never be counted as paid).
    const paid = Boolean(invoice.PaymentReceivedDate);
    // Past Due means: unpaid AND due date < today.
    const isPastDue = !paid && Boolean(dueDate) && dueDate.isBefore(today, "day");

    // Three MUTUALLY EXCLUSIVE buckets (Amine, 2026-10-19): an invoice is
    // Paid, or Past Due, or Unpaid — never two of them. Unpaid therefore
    // means "not paid and not yet past due", so countPaid + countUnpaid +
    // countBeyondDueDate always equals the number of visible rows.
    if (paid) {
      totalPaid += amount;
      countPaid++;
    } else if (isPastDue) {
      totalBeyondDueDate += amount;
      countBeyondDueDate++;
    } else {
      totalUnpaid += amount;
      countUnpaid++;
    }
  });

  const listOfTotals = [
    { name: "Paid: " + countPaid + " Invoices", total: totalPaid },
    { name: "Unpaid: " + countUnpaid + " Invoices", total: totalUnpaid },
    { name: "Past Due: " + countBeyondDueDate + " Invoices", total: totalBeyondDueDate },
  ];

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No invoices data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const csvRows = columnFilteredData
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            value = row[col.field];
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `invoices-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No invoices data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              showSnackbar("Preparing XLSX export…", theme, "info");

              // Create workbook and worksheet
              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Invoices");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              columnFilteredData.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      value = row[col.field];
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `invoices-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters,
      onClear,
      theme,
      isMobileToolbar,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const openExportSelected = () => {
        if (!selectionModel.length) return;
        const selectedInvoices = (listOfInvoices || []).filter((inv) =>
          selectionModel.includes(inv.id),
        );
        if (!selectedInvoices.length) return;
        setOverlayData({
          invoices: selectedInvoices,
          exportOptions: {},
        });
        setOverlayName(OVERLAY_NAMES.INVOICES_EXPORT);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobileToolbar && selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectionModel([]);
                  setSelectionEnabled(false);
                }}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={openExportSelected}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Generate PDFs
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (!selectionModel.length) return;
                  openMarkPaidDialogForInvoices(selectionModel);
                }}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Mark As Paid
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (!paidSelectedInvoiceIds.length) return;
                  openUnmarkPaidDialogForInvoices(paidSelectedInvoiceIds);
                }}
                disabled={paidSelectedInvoiceIds.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Mark As Unpaid
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (!selectionModel.length) return;
                  openUpdateDatesDialogForInvoices(selectionModel);
                }}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Update Dates
              </Button>
            </>
          )}

          {isMobileToolbar && selectionEnabled && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>

              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 220 }}>
                  <MenuItem
                    onClick={() => {
                      setSelectionModel([]);
                      setSelectionEnabled(false);
                      handleClose();
                    }}
                  >
                    <ListItemText>Cancel Selection</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      openExportSelected();
                      handleClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemText>Generate PDFs</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      if (!selectionModel.length) return;
                      openMarkPaidDialogForInvoices(selectionModel);
                      handleClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemText>Mark As Paid</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      if (!paidSelectedInvoiceIds.length) return;
                      openUnmarkPaidDialogForInvoices(
                        paidSelectedInvoiceIds,
                      );
                      handleClose();
                    }}
                    disabled={paidSelectedInvoiceIds.length === 0}
                  >
                    <ListItemText>Mark As Unpaid</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      if (!selectionModel.length) return;
                      openUpdateDatesDialogForInvoices(selectionModel);
                      handleClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemText>Update Dates</ListItemText>
                  </MenuItem>
                </Box>
              </Popover>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {/**
          <GridToolbarExport
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{
              fileName: `Invoices_${dayjs().format("YYYY-MM-DD")}`,
              utf8WithBom: true,
            }}
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHoverSelected,
                color: theme.palette.background.alt,
              },
            }}
          /> */}

            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobileToolbar} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  borderColor: theme.palette.divider,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    selectionEnabled,
    selectionModel,
    listOfInvoices,
    setOverlayData,
    setOverlayName,
    paidSelectedInvoiceIds,
  ]);

  // Batch C wave 2 — hoisted out of customFooter so the SAME element tree
  // renders in both the desktop grid footer and card mode (mobile.footer).
  const totalsStrip = (
    <Box
      sx={{
        p: "1rem",
        display: "flex",
        flexDirection: "row",
        gap: 2,
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": {
          height: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.primary.main,
          borderRadius: "3px",
          opacity: 0.5,
        },
      }}
    >
      {listOfTotals.map((total) => {
        return (
          <Box key={`${total}-${total.name}`} sx={{ flexShrink: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ whiteSpace: "nowrap" }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {total.name}:
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                }}
              >
                ${Number(total.total).toLocaleString()}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        {totalsStrip}

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  // ── Batch C wave 2 — card-mode filter sheet ────────────────────────────────
  // One entry per visible column that carries a header ColumnFilter /
  // ColumnDateFilter, built from the exact same props as those header
  // renderers so filter semantics are identical between modes. In card mode
  // the desktop headers are not rendered, so these sheet copies are the only
  // live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredColumns.find((col) => col.field === field);
    const dataIndex = filterColumns.findIndex((col) => col.field === field);
    const FilterComponent = CARD_MODE_DATE_FILTER_FIELDS.includes(field)
      ? ColumnDateFilter
      : ColumnFilter;
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <FilterComponent
          data={dataIndex >= 0 ? filterData[dataIndex] || [] : []}
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          onSortChange={(sortField, direction) => {
            const newSortModel = direction
              ? [{ field: sortField, sort: direction }]
              : [];
            setSortModel(newSortModel);
            handleSortModelChange(newSortModel);
          }}
          currentSort={sortModel.find((s) => s.field === field)?.sort || null}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredColumns
    .filter((col) =>
      filterColumns.some((filterCol) => filterCol.field === col.field),
    )
    .map((col) => {
      const selected = columnFilters[col.field] || [];
      return {
        field: col.field,
        headerName: col.headerName || col.field,
        summary: selected.length > 0 ? `${selected.length} selected` : "All",
        active: selected.length > 0,
        node: buildMobileFilterNode(col.field),
      };
    });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (§3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: handleToolbarClear,
    });
  }

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              gap={2}
            >
              <Box sx={{ width: "100%", maxWidth: FILTER_SELECTOR_WIDTH }}>
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={true}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>

              {!isMobile ? (
                <Button
                  onClick={() => {
                    const data = {
                      Clients: listOfClients,
                      ClientsOPIMinimums: listOfClientsOPIMinimums,
                      Linguists: listOfLinguists,
                      Languages: listOfLanguages,
                      //Interpretations: listOfServices,
                      InterpretationStatuses: listOfStatuses,
                      InterpretationTypes: listOfTypes,

                      Action: OVERLAY_NAMES.INVOICES_ACTION_NEW,
                    };

                    setOverlayData(data);

                    setOverlayName(OVERLAY_NAMES.INVOICES_NEW_EDIT);
                  }}
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Add Invoice</Box>
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    const data = {
                      Clients: listOfClients,
                      ClientsOPIMinimums: listOfClientsOPIMinimums,
                      Linguists: listOfLinguists,
                      Languages: listOfLanguages,
                      //Interpretations: listOfServices,
                      InterpretationStatuses: listOfStatuses,
                      InterpretationTypes: listOfTypes,

                      Action: OVERLAY_NAMES.INVOICES_ACTION_NEW,
                    };

                    setOverlayData(data);

                    setOverlayName(OVERLAY_NAMES.INVOICES_NEW_EDIT);
                  }}
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <AddRoundedIcon />
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          boxShadow: theme.shadows[3],
        }}
      >
        <DataGridResponsive
          loading={isLoading}
          mobile={{
            // Card mode (mobile) config — desktop grid ignores this prop.
            // 2026-10-05 round 4 (Amine: "The Show N More is only showing the
            // Note, and not showing the Date, Due Date, Late By, PaymentDate
            // and the OPI columns") — the four previously hidden fields now
            // render BEHIND the expander instead of being dropped.
            // The collapsed card keeps the approved compact summary —
            // Client / Late By / Amount — and the expander reveals
            // Date, Due Date, Payment Date, OPI, Note in desktop column
            // order (bodyOrder pins that exact card order; Late By only when
            // its value isn't N/A via hideNAFields).
            // 2026-10-05 round 3 (Amine: "I don't see the 'Show more' button
            // on the cards"): hideNAFields is SCOPED to "Late By". Unscoped it
            // dropped every "N/A" body field including Note, and invoices
            // rarely carry a Note (DB check: 20 of 345 in 2026, 0 of the 10
            // most recent), so the expander's only payload filtered away and
            // the button never rendered. Note always renders inside the
            // expanded card ("N/A" when empty - same as the desktop grid), so
            // "Show 5 more" is stable on every card.
            primaryField: "Number",
            badgeField: "Paid",
            hiddenFields: [],
            hideNAFields: ["DueDate2"],
            // Body fields = Client, Late By, Amount, Date, Due Date, Payment
            // Date, OPI, Note (Number is the headline, Paid the status badge).
            // bodyOrder keeps that exact card order; 3 collapsed (Client /
            // Late By / Amount) + 5 expandable (the date columns + OPI +
            // Note).
            bodyOrder: [
              "Client",
              "DueDate2",
              "Amount",
              "Date",
              "DueDate",
              "PaymentReceivedDate",
              "OPI",
              "Note",
            ],
            collapsedAfter: 3,
            pageSize: 20,
            selectable: true,
            // 2026-10-05 (Amine): mobile has no right-click — the card's
            // top-right ⋮ replays this screen's context menu (same handler,
            // same items and disabled states).
            contextMenuButton: true,
            // Batch C wave 2 — card-mode filter sheet: entries reuse the exact
            // header ColumnFilter / ColumnDateFilter props, activeCount drives
            // the toolbar badge, and Clear all reuses the existing toolbar
            // clear (column filters + quick filter, design §3b).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: handleToolbarClear,
            // Totals strip: the same element tree as the desktop grid footer
            // (hoisted above into `totalsStrip`).
            footer: totalsStrip,
            // Removable chips under the control row (wrapper adds the search
            // chip itself).
            chips: mobileFilterChips,
          }}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionEnabled ? selectionModel : []}
          onRowSelectionModelChange={(newSelection) => {
            if (!selectionEnabled) return;
            setSelectionModel(newSelection);
          }}
          disableRowSelectionOnClick={!!openFilter || selectionEnabled}
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          apiRef={apiRef}
          density={density}
          columns={filteredColumns}
          disableColumnMenu
          disableColumnSorting
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // A long-press opens a 700ms suppression WINDOW synchronously,
            // while the `selectionEnabled` state update may not have rendered
            // yet; the click that follows the long-press must neither toggle
            // selection nor open the invoice. Outside the window the gate is
            // structurally closed (round 7: boolean → timestamp + single-
            // shot reset) — a stale guard can never eat a plain tap.
            if (Date.now() < suppressClickUntilRef.current) {
              event.defaultMuiPrevented = true;
              suppressClickUntilRef.current = 0;
              return;
            }

            if (selectionEnabled) {
              setSelectionModel((prev) =>
                prev.includes(params.row.id)
                  ? prev.filter((id) => id !== params.row.id)
                  : [...prev, params.row.id],
              );
              return;
            }

            // Blanket card rule 2026-10-05 (Amine) — one tap opens the invoice
            // detail; the desktop click path shares the same destination.
            const data = {
              ComingFrom: `/invoices`,
            };
            navigate(`/invoices/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              showQuickFilter: true,
              hasAnyActiveFilters,
              onClear: handleToolbarClear,
              theme,
              isMobileToolbar: isMobile,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",

            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            height: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
            [`.${gridClasses.cell}`]: {
              color: theme.palette.primary.text,
            },
            [`.${gridClasses.cell}.notAvailable`]: {
              color: theme.palette.primary.text3,
            },
          }}
        />

        <Dialog
          theme={theme}
          openDialog={markPaidDialogOpen}
          title="Mark As Paid"
          customContent={
            <Stack spacing={2}>
              <Typography variant="body1">
                Are you sure you want to mark the selected{" "}
                {markPaidTargets.length} invoice(s) as paid?
              </Typography>
              <CEDatePicker
                label="Invoice Paid on"
                value={markPaidDate}
                onChange={(newValue) => setMarkPaidDate(newValue || dayjs())}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
          }
          secondaryAction="Cancel"
          secondaryOnClick={closeMarkPaidDialog}
          primaryAction={isMarkPaidSubmitting ? "Saving..." : "Yes, Mark Paid"}
          primaryOnClick={handleConfirmMarkPaid}
        />

        <Dialog
          theme={theme}
          openDialog={unmarkPaidDialogOpen}
          title="Mark As Unpaid"
          customContent={
            <Stack spacing={2}>
              <Typography variant="body1">
                Un-mark {unmarkPaidTargets.length} invoice(s) as paid? The
                payment date is cleared, the linked services are released back
                to unpaid, and the invoices can be edited again.
              </Typography>
              <TextField
                label="Reason (optional)"
                value={unmarkPaidReason}
                onChange={(event) => setUnmarkPaidReason(event.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Stack>
          }
          secondaryAction="Cancel"
          secondaryOnClick={closeUnmarkPaidDialog}
          primaryAction={
            isUnmarkPaidSubmitting ? "Saving..." : "Yes, Mark Unpaid"
          }
          primaryOnClick={handleConfirmUnmarkPaid}
        />

        <Dialog
          theme={theme}
          openDialog={updateDatesDialogOpen}
          title="Update Invoice Dates"
          customContent={
            <Stack spacing={2}>
              <Typography variant="body1">
                Update the Date and Due Date for the selected{" "}
                {updateDatesTargets.length} invoice(s).
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The Due Date will be automatically set to 30 days after the
                selected Date.
              </Typography>
              <CEDatePicker
                label="Invoice Date"
                value={updateDatesDate}
                onChange={(newValue) => setUpdateDatesDate(newValue || dayjs())}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <Typography variant="body2" color="text.secondary">
                Due Date will be:{" "}
                {(updateDatesDate || dayjs())
                  .add(30, "day")
                  .format("MM-DD-YYYY")}
              </Typography>
            </Stack>
          }
          secondaryAction="Cancel"
          secondaryOnClick={closeUpdateDatesDialog}
          primaryAction={
            isUpdateDatesSubmitting ? "Updating..." : "Update Dates"
          }
          primaryOnClick={handleConfirmUpdateDates}
        />
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            overlayAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Invoices;
